import { Event } from '../types';

export type SportFilter = 'all' | 'cricket' | 'football';
export type StatusFilter = 'all' | 'live' | 'scheduled' | 'completed';

export const getCategoryIcon = (category?: string): string => {
    if (category === 'Cricket') return '🏏';
    if (category === 'Football') return '⚽';
    return '🎬';
};

export const getCategoryLabel = (category?: string): string => {
    if (category === 'Cricket') return 'ক্রিকেট';
    if (category === 'Football') return 'ফুটবল';
    return 'খেলাধুলা';
};

export const getStatusLabel = (status?: string): string => {
    if (status === 'live') return 'লাইভ';
    if (status === 'scheduled') return 'আসন্ন';
    if (status === 'completed') return 'সম্পন্ন';
    return 'আসন্ন';
};

export const sortEvents = (events: Event[]): Event[] =>
    [...events].sort((a, b) => {
        if (a.status === 'live' && b.status !== 'live') return -1;
        if (a.status !== 'live' && b.status === 'live') return 1;
        const upvoteDiff = (b.upvotes || 0) - (a.upvotes || 0);
        if (upvoteDiff !== 0) return upvoteDiff;
        return new Date(`${a.date} ${a.time}`).getTime() - new Date(`${b.date} ${b.time}`).getTime();
    });

export const filterEvents = (
    events: Event[],
    options: {
        sportTab: SportFilter;
        statusFilter: StatusFilter;
        searchQuery: string;
        favoritesOnly?: boolean;
        favoriteIds?: Set<string>;
        excludeStatuses?: Array<Event['status']>;
        excludeIds?: Set<string>;
    }
): Event[] => {
    const query = options.searchQuery.trim().toLowerCase();

    return events.filter((event) => {
        if (options.excludeIds?.has(event.id)) return false;
        if (options.excludeStatuses?.includes(event.status)) return false;
        if (options.favoritesOnly && options.favoriteIds && !options.favoriteIds.has(event.id)) {
            return false;
        }
        if (options.statusFilter !== 'all' && event.status !== options.statusFilter) {
            return false;
        }
        if (options.sportTab === 'cricket' && event.category !== 'Cricket') return false;
        if (options.sportTab === 'football' && event.category !== 'Football') return false;
        if (!query) return true;

        const haystack = [
            event.name,
            event.location,
            event.category,
            event.teamA,
            event.teamB,
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

        return haystack.includes(query);
    });
};

/** Events for the main list when live carousel is visible — no duplicate live rows */
export const buildHomeListEvents = (
    events: Event[],
    options: {
        sportTab: SportFilter;
        statusFilter: StatusFilter;
        searchQuery: string;
        favoritesOnly?: boolean;
        favoriteIds?: Set<string>;
        liveCarouselVisible: boolean;
    }
): Event[] => {
    const excludeStatuses: Array<Event['status']> = [];

    if (options.liveCarouselVisible && options.statusFilter === 'all') {
        excludeStatuses.push('live');
    }

    const filtered = filterEvents(events, {
        ...options,
        excludeStatuses: excludeStatuses.length ? excludeStatuses : undefined,
    });

    return sortEvents(filtered);
};

export const shareEvent = async (event: Event): Promise<boolean> => {
    const url = `${window.location.origin}/event/${event.id}`;
    const text = `${event.name} — BDLINK LIVE`;

    if (navigator.share) {
        try {
            await navigator.share({ title: event.name, text, url });
            return true;
        } catch {
            return false;
        }
    }

    try {
        await navigator.clipboard.writeText(url);
        return true;
    } catch {
        return false;
    }
};
